import {
  formatVelocityBcBands,
  normalizeVelocityBcBands,
  parseVelocityBcBands,
  serializeVelocityBcBands,
} from "./velocity-bands.js";
import {
  normalizeMvTemperatureTable,
  parseMvTemperatureTable,
  serializeMvTemperatureTable,
} from "./mv-temperature.js";

export const UNIT_PROFILES = Object.freeze(["imperial", "metric", "decimal"]);

export const unitProfileOptions = Object.freeze([
  { value: "imperial", label: "Imperial" },
  { value: "metric", label: "Metric" },
  { value: "decimal", label: "Decimal / technical" },
]);

const unit = (id, label, dimension, scale, options = {}) => Object.freeze({
  id,
  label,
  dimension,
  scale,
  offset: 0,
  digits: 6,
  ...options,
});

const units = Object.freeze({
  fahrenheit: unit("fahrenheit", "°F", "temperature", 5 / 9, { offset: 255.3722222222222, digits: 1 }),
  celsius: unit("celsius", "°C", "temperature", 1, { offset: 273.15, digits: 2 }),
  kelvin: unit("kelvin", "K", "temperature", 1, { digits: 2 }),

  inhg: unit("inhg", "in Hg", "pressure", 3386.389, { digits: 2 }),
  hpa: unit("hpa", "hPa", "pressure", 100, { digits: 2 }),
  kpa: unit("kpa", "kPa", "pressure", 1000, { digits: 3 }),

  percent: unit("percent", "%", "proportion", 0.01, { digits: 1 }),
  ratio: unit("ratio", "decimal", "proportion", 1, { digits: 5 }),

  inch: unit("inch", "in", "length", 0.0254, { digits: 4 }),
  foot: unit("foot", "ft", "length", 0.3048, { digits: 3 }),
  yard: unit("yard", "yd", "length", 0.9144, { digits: 3 }),
  millimeter: unit("millimeter", "mm", "length", 0.001, { digits: 3 }),
  centimeter: unit("centimeter", "cm", "length", 0.01, { digits: 3 }),
  meter: unit("meter", "m", "length", 1, { digits: 4 }),
  kilometer: unit("kilometer", "km", "length", 1000, { digits: 5 }),

  fps: unit("fps", "ft/s", "velocity", 0.3048, { digits: 1 }),
  mps: unit("mps", "m/s", "velocity", 1, { digits: 3 }),
  mph: unit("mph", "mph", "velocity", 0.44704, { digits: 1 }),
  kph: unit("kph", "km/h", "velocity", 1 / 3.6, { digits: 3 }),

  fpsPerFahrenheit: unit("fpsPerFahrenheit", "ft/s/°F",
    "velocityTemperatureSlope", 0.3048 / (5 / 9), { digits: 3 }),
  mpsPerCelsius: unit("mpsPerCelsius", "m/s/°C",
    "velocityTemperatureSlope", 1, { digits: 4 }),

  grain: unit("grain", "gr", "mass", 0.00006479891, { digits: 2 }),
  gram: unit("gram", "g", "mass", 0.001, { digits: 5 }),
  kilogram: unit("kilogram", "kg", "mass", 1, { digits: 7 }),

  degree: unit("degree", "°", "angle", Math.PI / 180, { digits: 5 }),
  moa: unit("moa", "MOA", "angle", Math.PI / 10800, { digits: 3 }),
  mrad: unit("mrad", "mrad", "angle", 0.001, { digits: 4 }),
  // Preserve the original C engine's 1/6400-turn result unit at the boundary.
  natoMil: unit("natoMil", "NATO mil", "angle", Math.PI / 3200, { digits: 4 }),
  radian: unit("radian", "rad", "angle", 1, { digits: 8 }),
  arcminute: unit("arcminute", "′", "angle", Math.PI / 10800, { digits: 5 }),
  arcsecond: unit("arcsecond", "″", "angle", Math.PI / 648000, { digits: 5 }),

  second: unit("second", "s", "time", 1, { digits: 6 }),
  millisecond: unit("millisecond", "ms", "time", 0.001, { digits: 3 }),
  minuteTime: unit("minuteTime", "min", "time", 60, { digits: 4 }),
  hourTime: unit("hourTime", "h", "time", 3600, { digits: 5 }),

  footPound: unit("footPound", "ft·lbf", "energy", 1.3558179483314, { digits: 1 }),
  joule: unit("joule", "J", "energy", 1, { digits: 1 }),
  kilojoule: unit("kilojoule", "kJ", "energy", 1000, { digits: 4 }),

  poundForceSecond: unit("poundForceSecond", "lbf·s", "momentum", 4.4482216152605, { digits: 4 }),
  newtonSecond: unit("newtonSecond", "N·s", "momentum", 1, { digits: 4 }),

  rpm: unit("rpm", "rpm", "rotationRate", 2 * Math.PI / 60, { digits: 0 }),
  revolutionsPerSecond: unit("revolutionsPerSecond", "rev/s", "rotationRate", 2 * Math.PI, { digits: 3 }),
  radiansPerSecond: unit("radiansPerSecond", "rad/s", "rotationRate", 1, { digits: 3 }),

  caliber: unit("caliber", "cal", "caliber", 1, { digits: 3 }),

  inchPerTurn: unit("inchPerTurn", "in/turn", "twist", 0.0254, { digits: 3 }),
  millimeterPerTurn: unit("millimeterPerTurn", "mm/turn", "twist", 0.001, { digits: 2 }),
  centimeterPerTurn: unit("centimeterPerTurn", "cm/turn", "twist", 0.01, { digits: 3 }),

  g1: unit("g1", "G1", "ballisticCoefficient", 1, { fieldValue: "1" }),
  g2: unit("g2", "G2", "ballisticCoefficient", 1, { fieldValue: "2" }),
  g5: unit("g5", "G5", "ballisticCoefficient", 1, { fieldValue: "3" }),
  g6: unit("g6", "G6", "ballisticCoefficient", 1, { fieldValue: "4" }),
  g7: unit("g7", "G7", "ballisticCoefficient", 1, { fieldValue: "5" }),
  g8: unit("g8", "G8", "ballisticCoefficient", 1, { fieldValue: "6" }),
  gl: unit("gl", "GL", "ballisticCoefficient", 1, { fieldValue: "7" }),
  gi: unit("gi", "GI", "ballisticCoefficient", 1, { fieldValue: "8" }),
});

const group = (canonical, available, imperial, metric, decimal) => Object.freeze({
  canonical,
  available: Object.freeze(available),
  profiles: Object.freeze({ imperial, metric, decimal }),
});

const groups = Object.freeze({
  temperature: group("fahrenheit", ["fahrenheit", "celsius", "kelvin"], "fahrenheit", "celsius", "celsius"),
  pressure: group("inhg", ["inhg", "hpa", "kpa"], "inhg", "hpa", "kpa"),
  proportion: group("percent", ["percent", "ratio"], "percent", "percent", "ratio"),
  footDistance: group("foot", ["foot", "meter", "yard", "kilometer"], "foot", "meter", "meter"),
  yardDistance: group("yard", ["yard", "meter", "kilometer", "foot"], "yard", "meter", "kilometer"),
  smallLength: group("inch", ["inch", "millimeter", "centimeter", "meter"], "inch", "millimeter", "centimeter"),
  projectileVelocity: group("fps", ["fps", "mps", "kph"], "fps", "mps", "mps"),
  velocitySpread: group("fps", ["fps", "mps"], "fps", "mps", "mps"),
  velocityTemperatureSlope: group("fpsPerFahrenheit",
    ["fpsPerFahrenheit", "mpsPerCelsius"],
    "fpsPerFahrenheit", "mpsPerCelsius", "mpsPerCelsius"),
  speed: group("mph", ["mph", "kph", "mps"], "mph", "kph", "mps"),
  mass: group("grain", ["grain", "gram", "kilogram"], "grain", "gram", "gram"),
  degreeAngle: group("degree", ["degree", "moa", "mrad", "natoMil", "radian"], "degree", "degree", "degree"),
  correctionAngle: group("moa", ["moa", "mrad", "natoMil", "degree", "radian"], "mrad", "mrad", "mrad"),
  dispersionAngle: group("moa", ["moa", "mrad"], "moa", "mrad", "mrad"),
  minuteAngle: group("arcminute", ["arcminute", "degree", "mrad", "natoMil", "radian"], "arcminute", "arcminute", "degree"),
  secondAngle: group("arcsecond", ["arcsecond", "degree", "mrad", "natoMil", "radian"], "arcsecond", "arcsecond", "degree"),
  time: group("second", ["second", "millisecond", "minuteTime", "hourTime"], "second", "second", "second"),
  duration: group("hourTime", ["hourTime", "minuteTime", "second"], "hourTime", "hourTime", "hourTime"),
  energy: group("footPound", ["footPound", "joule", "kilojoule"], "footPound", "joule", "kilojoule"),
  momentum: group("poundForceSecond", ["poundForceSecond", "newtonSecond"], "poundForceSecond", "newtonSecond", "newtonSecond"),
  rotationRate: group("rpm", ["rpm", "revolutionsPerSecond", "radiansPerSecond"], "rpm", "rpm", "radiansPerSecond"),
  caliber: group("caliber", ["caliber"], "caliber", "caliber", "caliber"),
  twist: group("inchPerTurn", ["inchPerTurn", "millimeterPerTurn", "centimeterPerTurn"], "inchPerTurn", "millimeterPerTurn", "centimeterPerTurn"),
  ballisticCoefficient: group("g1", ["g1", "g2", "g5", "g6", "g7", "g8", "gl", "gi"], "g1", "g1", "g1"),
});

const groupCorrelations = new Map(
  Object.entries(groups).map(([correlation, unitGroup]) => [unitGroup, correlation]),
);

function unitGroupCorrelation(unitGroup) {
  return unitGroup?.correlation || groupCorrelations.get(unitGroup) || null;
}

const sourceGroups = Object.freeze({
  "°F": groups.temperature,
  "in Hg": groups.pressure,
  "%": groups.proportion,
  ft: groups.footDistance,
  yd: groups.yardDistance,
  in: groups.smallLength,
  "ft/s": groups.projectileVelocity,
  "ft/s/°F": groups.velocityTemperatureSlope,
  mph: groups.speed,
  gr: groups.mass,
  "°": groups.degreeAngle,
  MOA: groups.correctionAngle,
  "′": groups.minuteAngle,
  "″": groups.secondAngle,
  s: groups.time,
  cal: groups.caliber,
  "in/turn": groups.twist,
  G1: groups.ballisticCoefficient,
});

const withCanonical = (fieldGroup, canonical) => Object.freeze({
  ...fieldGroup,
  canonical,
  correlation: unitGroupCorrelation(fieldGroup),
});

const resultSourceGroups = Object.freeze({
  "°f": groups.temperature,
  "�f": groups.temperature,
  "ￂﾰf": groups.temperature,
  "in hg": groups.pressure,
  "%": groups.proportion,
  "% of sea level": groups.proportion,
  ft: groups.footDistance,
  foot: groups.footDistance,
  feet: groups.footDistance,
  yd: groups.yardDistance,
  yard: groups.yardDistance,
  yards: groups.yardDistance,
  in: groups.smallLength,
  inch: groups.smallLength,
  inches: groups.smallLength,
  "ft/s": groups.projectileVelocity,
  "ft/sec": groups.projectileVelocity,
  fps: groups.projectileVelocity,
  mph: groups.speed,
  gr: groups.mass,
  grain: groups.mass,
  grains: groups.mass,
  degree: groups.degreeAngle,
  degrees: groups.degreeAngle,
  "°": groups.degreeAngle,
  moa: groups.correctionAngle,
  // Raw CGI values labelled "mil" use the historical 6400-per-turn convention.
  mil: withCanonical(groups.correctionAngle, "natoMil"),
  s: groups.time,
  sec: groups.time,
  secs: groups.time,
  second: groups.time,
  seconds: groups.time,
  h: groups.duration,
  hr: groups.duration,
  hour: groups.duration,
  hours: groups.duration,
  "ft-lb": groups.energy,
  "ft-lbs": groups.energy,
  "ft·lbf": groups.energy,
  "lb-sec": groups.momentum,
  "lbs-sec": groups.momentum,
  "lbf·s": groups.momentum,
  cal: groups.caliber,
});

function normalizeResultUnit(unitLabel) {
  return String(unitLabel ?? "")
    .trim()
    .replace(/^\((.*)\)$/, "$1")
    .trim()
    .toLowerCase();
}

export function normalizeUnitProfile(profile) {
  return UNIT_PROFILES.includes(profile) ? profile : "imperial";
}

export function getFieldUnitGroup(field) {
  return ["number", "number-list", "velocity-bc-list"].includes(field?.type)
    ? groups[field.unitGroup] || sourceGroups[field.unit] || null : null;
}

export function getFieldUnitCorrelation(field) {
  return unitGroupCorrelation(getFieldUnitGroup(field));
}

export function getFieldUnitOptions(field) {
  const fieldGroup = getFieldUnitGroup(field);
  return fieldGroup
    ? fieldGroup.available.map((id) => ({
      id,
      label: units[id].label,
      ...(units[id].fieldValue === undefined
        ? {} : { fieldValue: units[id].fieldValue }),
    }))
    : [];
}

export function getFieldUnit(field, profile = "imperial", override = null) {
  const fieldGroup = getFieldUnitGroup(field);
  if (!fieldGroup) return null;
  if (override && fieldGroup.available.includes(override)) return override;
  return fieldGroup.profiles[normalizeUnitProfile(profile)];
}

const mvTemperatureAxisFields = Object.freeze({
  temperature: Object.freeze({ type: "number", unit: "°F" }),
  velocity: Object.freeze({ type: "number", unit: "ft/s" }),
});

function mvTemperatureAxisField(axis) {
  const field = mvTemperatureAxisFields[axis];
  if (!field) throw new TypeError(`Unknown MV-Temp table axis “${axis}”.`);
  return field;
}

export function getMvTemperatureTableUnitOptions(axis) {
  return getFieldUnitOptions(mvTemperatureAxisField(axis));
}

export function getMvTemperatureTableUnits(profile = "imperial",
  temperatureOverride = null, velocityOverride = null) {
  return {
    temperatureUnit: getFieldUnit(
      mvTemperatureAxisFields.temperature, profile, temperatureOverride,
    ),
    velocityUnit: getFieldUnit(
      mvTemperatureAxisFields.velocity, profile, velocityOverride,
    ),
  };
}

export function mvTemperatureRowsFromCanonical(value, temperatureUnitId, velocityUnitId) {
  return normalizeMvTemperatureTable(parseMvTemperatureTable(value)).map((row) => ({
    temperature: formatUnitValue(convertUnitValue(
      row.temperatureFahrenheit, "fahrenheit", temperatureUnitId,
    ), temperatureUnitId),
    velocity: formatUnitValue(convertUnitValue(
      row.velocityFps, "fps", velocityUnitId,
    ), velocityUnitId),
  }));
}

export function mvTemperatureRowsToCanonical(rows, temperatureUnitId, velocityUnitId) {
  if (!Array.isArray(rows)) {
    throw new TypeError("MV-Temp table rows must be a list.");
  }
  return serializeMvTemperatureTable(rows.map((row) => ({
    temperatureFahrenheit: convertUnitValue(
      row?.temperature, temperatureUnitId, "fahrenheit",
    ),
    velocityFps: convertUnitValue(row?.velocity, velocityUnitId, "fps"),
  })));
}

export function getUnitLabel(unitId) {
  return units[unitId]?.label || unitId;
}

export function getResultUnitGroup(unitLabel, valueLabel = "") {
  const normalizedUnit = normalizeResultUnit(unitLabel);
  const normalizedLabel = String(valueLabel).replace(/:$/, "").trim().toLowerCase();
  if (normalizedLabel === "twist" && ["in", "inch", "inches"].includes(normalizedUnit)) {
    return groups.twist;
  }
  if (normalizedLabel === "rpm") return groups.rotationRate;
  return resultSourceGroups[normalizedUnit] || null;
}

export function getResultUnitCorrelation(unitLabel, valueLabel = "") {
  return unitGroupCorrelation(getResultUnitGroup(unitLabel, valueLabel));
}

function normalizedValueLabel(value) {
  return String(value ?? "")
    .replace(/:$/, "")
    .trim()
    .toLowerCase();
}

export function getCorrelatedResultUnit(fields, unitLabel, valueLabel,
  profile = "imperial", fieldOverrides = {}, preferredUnit = null) {
  const resultGroup = getResultUnitGroup(unitLabel, valueLabel);
  const correlation = unitGroupCorrelation(resultGroup);
  if (!resultGroup || !correlation) return null;

  const candidates = (fields || []).flatMap((field) => {
    if (getFieldUnitCorrelation(field) !== correlation) return [];
    const override = fieldOverrides[field.name];
    const unitId = getFieldUnit(field, profile, override);
    return resultGroup.available.includes(unitId) ? [{ field, unitId, override }] : [];
  });
  if (candidates.length === 0) return null;

  const normalizedResultLabel = normalizedValueLabel(valueLabel);
  const exact = candidates.find(({ field }) => (
    normalizedValueLabel(field.label) === normalizedResultLabel
  ));
  if (exact) return exact.unitId;
  if (preferredUnit && resultGroup.available.includes(preferredUnit)) return preferredUnit;

  const manuallySelected = candidates.filter(({ override }) => Boolean(override));
  if (manuallySelected.length === 1) return manuallySelected[0].unitId;

  const selectedUnits = new Set(candidates.map(({ unitId }) => unitId));
  if (selectedUnits.size === 1) return candidates[0].unitId;
  return manuallySelected.at(-1)?.unitId || candidates[0].unitId;
}

export function getResultUnitOptions(unitLabel, valueLabel = "") {
  const resultGroup = getResultUnitGroup(unitLabel, valueLabel);
  return resultGroup
    ? resultGroup.available.map((id) => ({ id, label: units[id].label }))
    : [];
}

export function getResultUnit(unitLabel, valueLabel, profile = "imperial", override = null,
  correlatedUnit = null) {
  const resultGroup = getResultUnitGroup(unitLabel, valueLabel);
  if (!resultGroup) return null;
  if (override && resultGroup.available.includes(override)) return override;
  if (correlatedUnit && resultGroup.available.includes(correlatedUnit)) return correlatedUnit;
  return resultGroup.profiles[normalizeUnitProfile(profile)];
}

export function resultValueFromCanonical(unitLabel, valueLabel, value, unitId) {
  const resultGroup = getResultUnitGroup(unitLabel, valueLabel);
  return resultGroup
    ? formatUnitValue(convertUnitValue(value, resultGroup.canonical, unitId), unitId)
    : String(value ?? "");
}

export function convertUnitValue(value, fromUnitId, toUnitId) {
  if (value === "" || value === null || value === undefined) return "";
  const from = units[fromUnitId];
  const to = units[toUnitId];
  const numericValue = Number(value);
  if (!from || !to || from.dimension !== to.dimension) {
    throw new TypeError(`Cannot convert ${fromUnitId} to ${toUnitId}.`);
  }
  if (!Number.isFinite(numericValue)) throw new TypeError("Unit value must be finite.");
  const baseValue = numericValue * from.scale + from.offset;
  return (baseValue - to.offset) / to.scale;
}

export function convertUnitDelta(value, fromUnitId, toUnitId) {
  const from = units[fromUnitId];
  const to = units[toUnitId];
  const numericValue = Number(value);
  if (!from || !to || from.dimension !== to.dimension) {
    throw new TypeError(`Cannot convert ${fromUnitId} to ${toUnitId}.`);
  }
  if (!Number.isFinite(numericValue)) throw new TypeError("Unit delta must be finite.");
  return numericValue * from.scale / to.scale;
}

function cleanNumber(value, digits) {
  if (value === "") return "";
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "";
  const rounded = Number(numericValue.toFixed(digits));
  return Object.is(rounded, -0) ? "0" : String(rounded);
}

export function formatUnitValue(value, unitId) {
  return cleanNumber(value, units[unitId]?.digits ?? 8);
}

export function serializeUnitValue(value) {
  if (value === "" || value === null || value === undefined) return "";
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "";
  const rounded = Number(numericValue.toPrecision(12));
  return Object.is(rounded, -0) ? "0" : String(rounded);
}

export function fieldValueFromCanonical(field, value, unitId) {
  const fieldGroup = getFieldUnitGroup(field);
  if (field?.unitField) return String(value ?? "");
  if (field?.type === "velocity-bc-list") {
    try {
      const bands = normalizeVelocityBcBands(parseVelocityBcBands(value));
      return formatVelocityBcBands(bands, (velocity) => formatUnitValue(
        convertUnitValue(velocity, fieldGroup.canonical, unitId), unitId,
      ));
    } catch (_) {
      return String(value ?? "");
    }
  }
  if (field?.type === "number-list") {
    const values = String(value ?? "").trim().split(/[\s,;]+/).filter(Boolean);
    if (values.length === 0 || values.some((item) => !Number.isFinite(Number(item)))) {
      return String(value ?? "");
    }
    return values.map((item) => formatUnitValue(convertUnitValue(
      item, fieldGroup.canonical, unitId), unitId)).join(", ");
  }
  return fieldGroup
    ? formatUnitValue(convertUnitValue(value, fieldGroup.canonical, unitId), unitId)
    : String(value ?? "");
}

export function fieldValueToCanonical(field, value, unitId) {
  const fieldGroup = getFieldUnitGroup(field);
  if (field?.unitField) return String(value ?? "");
  if (!fieldGroup) return String(value ?? "");
  if (value === "" || value === null || value === undefined) return "";

  if (field?.type === "velocity-bc-list") {
    try {
      return velocityBcFieldValueToCanonical(field, value, unitId);
    } catch (_) {
      return "";
    }
  }

  if (field?.type === "number-list") {
    const values = String(value).trim().split(/[\s,;]+/).filter(Boolean);
    if (values.length === 0 || values.some((item) => !Number.isFinite(Number(item)))) {
      return "";
    }
    return values.map((item) => serializeUnitValue(convertUnitValue(
      item, unitId, fieldGroup.canonical))).join(",");
  }

  let converted = convertUnitValue(value, unitId, fieldGroup.canonical);
  const displayed = Number(value);
  for (const boundary of ["min", "max"]) {
    if (field[boundary] === undefined) continue;
    const displayedBoundary = Number(formatUnitValue(convertUnitValue(
      field[boundary], fieldGroup.canonical, unitId), unitId));
    if (displayed === displayedBoundary) converted = Number(field[boundary]);
  }
  return serializeUnitValue(converted);
}

export function velocityBcFieldValueToCanonical(field, value, unitId) {
  const fieldGroup = getFieldUnitGroup(field);
  if (field?.type !== "velocity-bc-list" || !fieldGroup) {
    throw new TypeError("Velocity-banded BC field configuration is invalid.");
  }
  const converted = parseVelocityBcBands(value).map(({ velocity, bc }) => ({
    velocity: convertUnitValue(velocity, unitId, fieldGroup.canonical),
    bc,
  }));
  return serializeVelocityBcBands(normalizeVelocityBcBands(converted));
}

export function fieldDisplayConstraints(field, unitId) {
  const fieldGroup = getFieldUnitGroup(field);
  if (field?.unitField) return { min: field.min, max: field.max, step: field.step };
  if (!fieldGroup) return { min: field.min, max: field.max, step: field.step };

  // Step is an editing convenience, not a validity rule. A value entered in
  // one unit can legitimately land between the canonical unit's spinner
  // increments after conversion (for example, millimetres converted back to
  // inches). Never let that presentation detail invalidate stored data.
  const step = field.step === undefined ? undefined : "any";

  return {
    min: field.min === undefined ? undefined : formatUnitValue(
      convertUnitValue(field.min, fieldGroup.canonical, unitId), unitId),
    max: field.max === undefined ? undefined : formatUnitValue(
      convertUnitValue(field.max, fieldGroup.canonical, unitId), unitId),
    step,
  };
}
